/*
 * Hover cards for the header nav.
 *
 * Matches each <template data-match="..."> from sections/gn-nav-tips.liquid to a
 * nav link by its visible label, then shows a single reused card element under
 * whichever link the visitor is pointing at.
 *
 * Design notes worth keeping:
 *
 * - ONE card element, moved and refilled. Rendering a card per nav item would
 *   put six hidden images in the header on every page load.
 *
 * - position: fixed. The header is sticky and clips its children, so an
 *   absolutely positioned flyout gets cut off at the header's edge.
 *
 * - A close DELAY, not an immediate hide. The gap between the link and the card
 *   is dead space; hiding on mouseleave means the card vanishes as the pointer
 *   travels toward it. The card also cancels the pending close on enter, which
 *   is what makes its link clickable at all.
 *
 * - Desktop pointers only. On touch, `mouseenter` fires on first tap and the
 *   card would eat the tap that was meant to follow the link.
 */
(() => {
  'use strict';

  const OPEN_DELAY = 140;
  const CLOSE_DELAY = 180;

  const host = document.querySelector('[data-gn-navtips]');
  if (!host) return;

  // Coarse pointers never get a card; bail before wiring anything up.
  if (window.matchMedia('(hover: none), (pointer: coarse)').matches) return;

  const tips = new Map();
  for (const tpl of host.querySelectorAll('[data-gn-navtip]')) {
    const key = (tpl.dataset.match || '').trim().toLowerCase();
    if (key) tips.set(key, tpl);
  }
  if (!tips.size) return;

  let layer = null;
  let openTimer = null;
  let closeTimer = null;
  let current = null;

  function ensureLayer() {
    if (layer) return layer;
    layer = document.createElement('div');
    layer.className = 'gn-navtip-layer';
    layer.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    layer.addEventListener('mouseleave', scheduleClose);
    layer.addEventListener('click', (e) => {
      // A link row is a real anchor — let it navigate itself. Without this the
      // handler below also fired and raced the anchor to a different URL.
      if (e.target.closest('a')) return;

      // The CTA is a span (it cannot be an anchor without nesting inside the
      // nav link's accessible name), so it is followed explicitly.
      const cta = e.target.closest('.gn-navtip__cta');
      if (cta && cta.dataset.href) {
        window.location.href = cta.dataset.href;
        return;
      }

      // Blank space on a MENU card does nothing: the panel is a menu, and a
      // stray click on its padding should not navigate anywhere. On a plain
      // explainer card the whole surface stays a shortcut to the nav item.
      if (layer.querySelector('.gn-navtip__links')) return;
      const href = current && current.getAttribute('href');
      if (href && href !== '#') window.location.href = href;
    });
    document.body.appendChild(layer);
    return layer;
  }

  function place(link) {
    const el = ensureLayer();
    const r = link.getBoundingClientRect();
    const w = el.offsetWidth || 320;
    const margin = 12;

    // Left-align to the link, then clamp inside the viewport.
    let left = r.left;
    left = Math.min(left, window.innerWidth - w - margin);
    left = Math.max(margin, left);

    el.style.insetInlineStart = `${Math.round(left)}px`;
    el.style.insetBlockStart = `${Math.round(r.bottom + 10)}px`;

    // Keep the pointer over the link even after the card has been clamped.
    const arrow = Math.min(Math.max(r.left - left + r.width / 2 - 5, 14), w - 24);
    el.style.setProperty('--gn-navtip-arrow', `${Math.round(arrow)}px`);
  }

  function show(link) {
    const tpl = tips.get(label(link));
    if (!tpl) return;

    /* Flag here as well as in bindAll. Horizon's <overflow-list> re-renders the
       nav after load to fill its "More" menu, and a flag set during the initial
       pass did not survive — Resources kept opening its native mega menu behind
       the card. Doing it on open is the one moment the element is guaranteed to
       be the live one. */
    if (tpl.content.querySelector('.gn-navtip__links')) {
      const item = link.closest('li');
      if (item) item.setAttribute('data-gn-navtip-menu', '');
    }

    current = link;
    const el = ensureLayer();
    el.replaceChildren(tpl.content.cloneNode(true));
    place(link);
    el.setAttribute('data-open', '');
  }

  function close() {
    if (!layer) return;
    layer.removeAttribute('data-open');
    current = null;
  }

  function scheduleClose() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(close, CLOSE_DELAY);
  }

  /* A card carrying link rows IS the item's menu, not a tooltip beside it. Such
     an item behaves like a parent menu item everywhere else on the web: hover
     opens the panel, and clicking the parent opens the panel rather than
     navigating away from it. Items whose card has no rows keep their link and
     just get an explainer on hover. */
  function isMenuCard(link) {
    const key = label(link);
    const tpl = tips.get(key);
    return !!(tpl && tpl.content.querySelector('.gn-navtip__links'));
  }

  function label(link) {
    return link.textContent.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  function bind(link) {
    if (link.dataset.gnNavtipBound) return;
    link.dataset.gnNavtipBound = '1';

    if (isMenuCard(link)) {
      link.addEventListener('click', (e) => {
        // Leave modified clicks alone: the visitor asked for a new tab.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        show(link);
      });
    }

    link.addEventListener('mouseenter', () => {
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      openTimer = setTimeout(() => show(link), OPEN_DELAY);
    });
    link.addEventListener('mouseleave', scheduleClose);
    // Keyboard parity: focus opens immediately, blur closes on the same delay.
    link.addEventListener('focus', () => {
      clearTimeout(closeTimer);
      show(link);
    });
    link.addEventListener('blur', scheduleClose);
  }

  function bindAll() {
    for (const link of document.querySelectorAll('.menu-list__link')) {
      bind(link);
      /* Re-flagged on EVERY pass, not once at bind. Horizon's <overflow-list>
         re-renders the nav to move items into its "More" menu, which replaces
         the <li> elements — a flag set once at bind time disappeared with them,
         so Resources kept opening its native mega menu alongside the card.
         setAttribute is idempotent, so repeating it is free. */
      if (isMenuCard(link)) {
        const item = link.closest('li');
        if (item) item.setAttribute('data-gn-navtip-menu', '');
      }
    }
  }

  bindAll();

  // The header re-renders its overflow list on resize, replacing the links, so
  // rebind rather than assuming the first pass covered everything.
  const nav = document.querySelector('nav.menu-list');
  if (nav && 'MutationObserver' in window) {
    new MutationObserver(bindAll).observe(nav, { childList: true, subtree: true });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
  // A card pinned to a link that has scrolled away reads as a stray box.
  window.addEventListener('scroll', close, { passive: true });
  window.addEventListener('resize', close);
})();
